import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { Phone } from 'lucide-react';

import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ServiceRedirect from './components/ServiceRedirect';

// Only the Home route is eager; every other page is route-level code-split so its
// JS stays out of the main chunk.
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const MainServicePage = lazy(() => import('./pages/MainServicePage'));
const SubServicePage = lazy(() => import('./pages/SubServicePage'));
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CardPage = lazy(() => import('./pages/CardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ServiceAreasPage = lazy(() => import('./pages/ServiceAreasPage'));
const ServiceAreaTownPage = lazy(() => import('./pages/ServiceAreaTownPage'));

// Admin + portal routes are code-split so their JS stays out of the public bundle.
const AdminJobberPage = lazy(() => import('./pages/AdminJobberPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminCustomersPage = lazy(() => import('./pages/AdminCustomersPage'));
const AdminCustomerDetailPage = lazy(() => import('./pages/AdminCustomerDetailPage'));
const AdminMembershipsPage = lazy(() => import('./pages/AdminMembershipsPage'));
const AdminWarrantiesPage = lazy(() => import('./pages/AdminWarrantiesPage'));
const AdminRequestsPage = lazy(() => import('./pages/AdminRequestsPage'));
const AdminAuditPage = lazy(() => import('./pages/AdminAuditPage'));
const PortalOverviewPage = lazy(() => import('./pages/portal/PortalOverviewPage'));
const PortalProfilePage = lazy(() => import('./pages/portal/PortalProfilePage'));
const PortalRequestsPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalRequestsPage })));
const PortalWorkHistoryPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalWorkHistoryPage })));
const PortalAppointmentsPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalAppointmentsPage })));
const PortalQuotesPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalQuotesPage })));
const PortalInvoicesPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalInvoicesPage })));
const PortalPropertiesPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalPropertiesPage })));
const PortalEquipmentPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalEquipmentPage })));
const PortalWarrantiesPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalWarrantiesPage })));
const PortalMembershipPage = lazy(() => import('./pages/portal/PortalSubPages').then((m) => ({ default: m.PortalMembershipPage })));
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './context/ProtectedRoute';

import './App.css';
import { initAnalytics, trackPageView, trackClickToCall, trackSmsClick, trackEmailClick } from './lib/analytics';

const PHONE = "587-355-FLOW (3569)";

// Initialize analytics once (gtag injection + UTM capture)
initAnalytics();

// Scroll to top on route change + fire GA4 page_view
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const isFirst = React.useRef(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
    // Skip the fade on the very first (initial/prerendered) load; only
    // enable it once the user navigates client-side.
    if (isFirst.current) {
      isFirst.current = false;
    } else {
      document.body.classList.add('route-changed');
    }
  }, [pathname]);
  
  return null;
};

// Global delegated listener: one click handler fires the right analytics
// event for every tel:, sms: and mailto: anchor, anywhere in the app.
const LinkClickTracker = () => {
  useEffect(() => {
    const handler = (e) => {
      // Walk up the DOM to find the nearest anchor
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === 'A') {
          const href = el.getAttribute('href') || '';
          if (href.startsWith('tel:')) {
            trackClickToCall(href);
            break;
          }
          if (href.startsWith('sms:')) {
            trackSmsClick(href);
            break;
          }
          if (href.startsWith('mailto:')) {
            trackEmailClick(href);
            break;
          }
        }
        el = el.parentElement;
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);
  return null;
};

// Layout wrapper component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-grow">
        {children}
      </div>
      <Footer />
      
      {/* Sticky Mobile Call Button */}
      <a
        href={`tel:+15873553569`}
        data-testid="mobile-call-btn"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:hidden z-50 bg-[#DC2626] text-white font-bold px-8 py-4 rounded-full shadow-2xl flex items-center gap-2 hover:bg-[#B91C1C] transition-colors"
        aria-label="Call us"
      >
        <Phone className="w-5 h-5" />
        Call Now
      </a>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      {process.env.REACT_APP_GSC_VERIFICATION && (
        <Helmet>
          <meta name="google-site-verification" content={process.env.REACT_APP_GSC_VERIFICATION} />
        </Helmet>
      )}
      <BrowserRouter>
        <AuthProvider>
        <ScrollToTop />
        <LinkClickTracker />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#fff',
              color: '#111827',
              border: '1px solid #E5E7EB',
            },
          }}
        />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        
        {/* Static pages - must come before dynamic routes */}
        <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
        <Route path="/services/:serviceType" element={<ServiceRedirect />} />
        <Route path="/maintenance" element={<Layout><MembershipPage /></Layout>} />
        <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/thank-you" element={<Layout><ThankYouPage /></Layout>} />
        <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
        {/* Unpublished post — kept in source but not reachable; redirect to the blog index */}
        <Route path="/blog/ac-maintenance-tips" element={<Navigate to="/blog" replace />} />
        <Route path="/blog/:postId" element={<Layout><BlogPostPage /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/service-areas" element={<Layout><ServiceAreasPage /></Layout>} />
        <Route path="/service-areas/:town" element={<Layout><ServiceAreaTownPage /></Layout>} />

        {/* Portal auth — code-split pages need Suspense */}
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/portal"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalOverviewPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/profile"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalProfilePage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/requests"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalRequestsPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/work-history"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalWorkHistoryPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/appointments"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalAppointmentsPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/quotes"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalQuotesPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/invoices"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalInvoicesPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/properties"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalPropertiesPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/equipment"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalEquipmentPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/warranties"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalWarrantiesPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/portal/membership"
          element={<ProtectedRoute role="customer"><Suspense fallback={null}><PortalMembershipPage /></Suspense></ProtectedRoute>}
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminDashboardPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/customers"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminCustomersPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/customers/:customerId"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminCustomerDetailPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/memberships"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminMembershipsPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/warranties"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminWarrantiesPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/requests"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminRequestsPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/audit"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminAuditPage /></Suspense></ProtectedRoute>}
        />
        <Route
          path="/admin/settings/jobber"
          element={<ProtectedRoute role="admin"><Suspense fallback={null}><AdminJobberPage /></Suspense></ProtectedRoute>}
        />

        {/* Standalone QR business-card landing page (no header/footer) */}
        <Route path="/card" element={<CardPage />} />
        
        {/* Service Pages - must be last for dynamic routing */}
        <Route path="/:mainService/:subService" element={<Layout><SubServicePage /></Layout>} />
        <Route path="/:mainService" element={<Layout><MainServicePage /></Layout>} />
      </Routes>
      </Suspense>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
